import { useQuery, type QueryClient } from "@tanstack/react-query";
import {
  ConversationListResponse,
  ConversationView,
  DmSettings,
  MessagePage,
  SendMessageResponse,
  type DmPolicy,
} from "@townsy/contracts";
import { API_BASE, apiFetch, isApiError } from "./client";

/** Private messages (ADR-0014): the signed-in account's conversations. */
export const messageEndpoints = {
  conversations: `${API_BASE}/me/conversations`,
  conversation: (id: string) => `${API_BASE}/me/conversations/${encodeURIComponent(id)}`,
  messages: (id: string) => `${API_BASE}/me/conversations/${encodeURIComponent(id)}/messages`,
  read: (id: string) => `${API_BASE}/me/conversations/${encodeURIComponent(id)}/read`,
  settings: `${API_BASE}/me/dm-settings`,
};

export const messageKeys = {
  all: ["dm"] as const,
  conversations: ["dm", "conversations"] as const,
  messages: (id: string) => ["dm", "messages", id] as const,
  settings: ["dm", "settings"] as const,
};

export const fetchConversations = (): Promise<ConversationListResponse> => apiFetch(messageEndpoints.conversations, { schema: ConversationListResponse });
export const openConversation = (recipientActorId: string): Promise<ConversationView> =>
  apiFetch(messageEndpoints.conversations, { method: "POST", body: { recipientActorId }, schema: ConversationView });
export const fetchMessages = (id: string): Promise<MessagePage> => apiFetch(`${messageEndpoints.messages(id)}?limit=100`, { schema: MessagePage });
export const sendMessage = (id: string, text: string, clientMessageId: string): Promise<SendMessageResponse> =>
  apiFetch(messageEndpoints.messages(id), { method: "POST", body: { text, clientMessageId }, schema: SendMessageResponse });
export const markRead = async (id: string): Promise<void> => {
  await apiFetch(messageEndpoints.read(id), { method: "POST", body: {} });
};
export const fetchDmSettings = (): Promise<DmSettings> => apiFetch(messageEndpoints.settings, { schema: DmSettings });
export const putDmSettings = (policy: DmPolicy): Promise<DmSettings> => apiFetch(messageEndpoints.settings, { method: "PUT", body: { policy }, schema: DmSettings });

const noAuthRetry = (count: number, err: unknown) => !(isApiError(err) && [401, 403, 404].includes(err.status)) && count < 2;

/** The conversation list (unread counts). Realtime notices refresh it at once; the interval is only a fallback. */
export function useConversationsQuery(enabled: boolean) {
  // Never served from cache when a list or thread opens (the app default keeps data 30 s): mutes and settings change
  // who can send, and that must show the moment the dialog opens.
  return useQuery({ queryKey: messageKeys.conversations, queryFn: fetchConversations, enabled, staleTime: 0, refetchInterval: enabled ? 60_000 : false, retry: noAuthRetry });
}

export function useMessagesQuery(conversationId: string | null) {
  return useQuery({
    queryKey: messageKeys.messages(conversationId ?? "none"),
    queryFn: () => fetchMessages(conversationId!),
    enabled: Boolean(conversationId),
    staleTime: 0,
    refetchInterval: conversationId ? 20_000 : false,
    retry: noAuthRetry,
  });
}

export function useDmSettingsQuery(enabled: boolean) {
  return useQuery({ queryKey: messageKeys.settings, queryFn: fetchDmSettings, enabled, retry: noAuthRetry });
}

/** After a notice or a send: re-read the list and that conversation. */
export function invalidateMessages(qc: QueryClient, conversationId?: string | null): void {
  void qc.invalidateQueries({ queryKey: messageKeys.conversations });
  if (conversationId) void qc.invalidateQueries({ queryKey: messageKeys.messages(conversationId) });
}
